import { createContext, FC, ReactNode, useContext } from "react"
import { useStaffChatStream } from "../../hooks/use-staff-chat-stream"
import { useAssistantFallbackTracking } from "../../hooks/use-assistant-fallback-tracking"

type StaffChatSession = ReturnType<typeof useStaffChatStream>

const StaffChatSessionContext = createContext<StaffChatSession | null>(null)

/**
 * One assistant session for the authenticated application shell. The drawer
 * and full-page workspace are two views over this state, so expanding a chat
 * never loses the active conversation or stream.
 */
export const StaffChatSessionProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const session = useStaffChatStream()
  // Lives here (not in the chat view) so it survives the drawer closing and
  // the page unmounting — that is exactly when a fallback happens.
  useAssistantFallbackTracking(session.messages, session.isStreaming)
  return (
    <StaffChatSessionContext.Provider value={session}>
      {children}
    </StaffChatSessionContext.Provider>
  )
}

export function useStaffChatSession(): StaffChatSession {
  const session = useContext(StaffChatSessionContext)
  if (!session) {
    throw new Error(
      "useStaffChatSession must be used within StaffChatSessionProvider"
    )
  }
  return session
}
